import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  LayoutDashboard, Users, Calendar, Video, BarChart, 
  MessageSquare, FileText, Settings, LogOut, Trophy, Image, ChevronDown, ChevronRight, Share2,
  PenTool, UserCircle, Contact, Bell, Building2, BarChart3, ShieldAlert
} from 'lucide-react';
import { BasketballGoalIcon } from '@/components/BasketballGoalIcon';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import axios from 'axios';
import GlobalVoiceCommand from '@/components/GlobalVoiceCommand';
import NotificationBell from '@/components/NotificationBell';
import HelpFeedbackWidget from '@/components/HelpFeedbackWidget';
import { Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { hasAccess, requiredTier, TIER_LABELS } from '@/lib/tiers';

export default function Layout({ children, user, onLogout, teams = [], onSwitchTeam }) {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState(['communication']);
  const [teamLogoUrl, setTeamLogoUrl] = useState(null);
  const [tier, setTier] = useState(null);
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [coachAccess, setCoachAccess] = useState(null);  // { [area]: 'none'|'view'|'edit' }
  const currentTeam = teams.find((t) => t.id === user.team_id);

  // Analytics is restricted to admin accounts + specific head coach emails.
  const ANALYTICS_ALLOWED_EMAILS = new Set(['autumn.p.fisher1985@gmail.com']);
  const canSeeAnalytics = (
    user.role === 'admin'
    || user.is_admin === true
    || ANALYTICS_ALLOWED_EMAILS.has((user.email || '').toLowerCase())
  );

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/payments/subscription/${user.team_id || 'none'}`)
      .then((res) => setTier(res.data?.tier || 'basic'))
      .catch(() => setTier('basic'));
  }, [user.team_id]);

  useEffect(() => {
    // Load per-area coach access so we can render a small lock icon on locked pages.
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/user/permissions?user_id=${user.id}&team_id=${user.team_id}`)
      .then((res) => {
        setCoachAccess(res.data?.access || null);
      })
      .catch(() => setCoachAccess(null));
  }, [user.id, user.team_id]);

  // Map sidebar hrefs to the permission area they represent.
  const HREF_TO_AREA = {
    '/players': 'players',
    '/schedule': 'schedule',
    '/practice': 'practice_plans',
    '/videos': 'videos',
    '/stats': 'stats',
    '/reports': 'reports',
    '/communication': 'communication',
    '/coaching-communication': 'communication',
    '/social-media': 'communication',
    '/automated-messages': 'communication',
    '/contacts': 'communication',
  };

  const permissionLevelForHref = (href) => {
    if (!coachAccess) return 'edit';
    const area = HREF_TO_AREA[href];
    if (!area) return 'edit';
    return coachAccess[area] || 'edit';
  };

  const isPermissionLocked = (href) => permissionLevelForHref(href) === 'none';
  const isPermissionViewOnly = (href) => permissionLevelForHref(href) === 'view';

  const isLocked = (href) => tier !== null && !hasAccess(tier, href);

  const handleLockedClick = (e, item) => {
    e.preventDefault();
    setUpgradeTarget(item);
  };

  useEffect(() => {
    let cancelled = false;
    const propLogo = currentTeam?.logo_url;
    if (propLogo) {
      setTeamLogoUrl(`${process.env.REACT_APP_BACKEND_URL}${propLogo}`);
    } else {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/api/teams/${user.team_id}`)
        .then((res) => {
          if (!cancelled) {
            setTeamLogoUrl(
              res.data?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${res.data.logo_url}` : null
            );
          }
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [user.team_id, currentTeam?.logo_url]);

  // Apply team brand colors to dashboard accents
  useEffect(() => {
    const hexToHslParts = (hex) => {
      const m = (hex || '').replace('#', '');
      if (!/^[0-9a-fA-F]{6}$/.test(m)) return null;
      const r = parseInt(m.slice(0, 2), 16) / 255;
      const g = parseInt(m.slice(2, 4), 16) / 255;
      const b = parseInt(m.slice(4, 6), 16) / 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0;
      let s = 0;
      const l = (max + min) / 2;
      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h /= 6;
      }
      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/team-branding/${user.team_id}`)
      .then((res) => {
        const root = document.documentElement;
        const primary = hexToHslParts(res.data?.primary_color);
        const accent = hexToHslParts(res.data?.accent_color);
        if (primary) root.style.setProperty('--primary', primary);
        if (accent) root.style.setProperty('--accent', accent);
      })
      .catch(() => {});
  }, [user.team_id]);

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => 
      prev.includes(sectionName) 
        ? prev.filter(s => s !== sectionName)
        : [...prev, sectionName]
    );
  };

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Schedule', href: '/schedule', icon: Calendar },
    { name: 'Practice', href: '/practice', icon: BasketballGoalIcon },
    { name: 'Players', href: '/players', icon: Users },
    { name: 'Whiteboard', href: '/whiteboard', icon: PenTool },
    { name: 'Stats', href: '/stats', icon: BarChart },
    { 
      name: 'Communication', 
      icon: MessageSquare,
      submenu: [
        { name: 'Coaching Staff', href: '/coaching-communication', icon: UserCircle },
        { name: 'Contact Cards', href: '/contacts', icon: Contact },
        { name: 'Automated Messages', href: '/automated-messages', icon: Bell },
        { name: 'Social Media', href: '/social-media', icon: Share2 }
      ]
    },
    { name: 'Reports', href: '/reports', icon: FileText },
    { name: 'Videos', href: '/videos', icon: Video },
    { name: 'Photos', href: '/photos', icon: Image },
    ...(canSeeAnalytics ? [{ name: 'Analytics', href: '/admin/analytics', icon: BarChart3 }] : []),
    ...((user.role === 'head_coach' || user.role === 'assistant_coach')
      ? [{ name: 'Safety Alerts', href: '/safety-escalations', icon: ShieldAlert }] : []),
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="flex">
        <aside className="w-64 min-h-screen bg-secondary text-secondary-foreground border-r border-white/10 flex flex-col">
          <div className="p-6 border-b border-white/10 leopard-texture">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img
                  src={teamLogoUrl || '/pac-logo.png'}
                  alt={currentTeam?.name || 'PAC logo'}
                  className="h-11 w-11 object-contain rounded"
                  data-testid="nav-logo"
                />
                <div>
                  <h2 className="font-heading text-xl font-bold uppercase tracking-tight">PAC</h2>
                  <p className="text-xs text-white/70">Personal AI Coach</p>
                </div>
              </div>
              <NotificationBell user={user} />
            </div>
          </div>
          
          {/* Team Selector */}
          {teams.length > 0 && (
            <div className="p-4 border-b border-white/10">
              <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block">
                <Building2 className="h-3 w-3 inline mr-1" />
                Current Team
              </label>
              <Select 
                value={user.team_id} 
                onValueChange={(value) => onSwitchTeam && onSwitchTeam(value)}
              >
                <SelectTrigger className="w-full bg-white/10 border-white/20 text-white" data-testid="team-selector">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <nav className="p-4 space-y-1 flex-1 overflow-y-auto pb-8">
            {navigation.map((item, index) => {
              // Handle divider/spacer
              if (item.type === 'divider') {
                return <div key={`divider-${index}`} className="my-4 border-t border-white/10" />;
              }
              
              const Icon = item.icon;
              const isActive = item.href ? location.pathname === item.href : false;
              const hasSubmenu = item.submenu && item.submenu.length > 0;
              const isExpanded = expandedSections.includes(item.name.toLowerCase().replace(' ', ''));
              
              if (hasSubmenu) {
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleSection(item.name.toLowerCase().replace(' ', ''))}
                      className={cn(
                        'flex items-center justify-between w-full px-4 py-3 rounded-sm font-medium transition-all duration-200',
                        'text-white/80 hover:bg-white/10 hover:text-white'
                      )}
                      data-testid={`nav-${item.name.toLowerCase()}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        <span className="text-sm uppercase tracking-wide">{item.name}</span>
                      </div>
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    {isExpanded && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = location.pathname === subItem.href;
                          const subLocked = isLocked(subItem.href);
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              onClick={subLocked ? (e) => handleLockedClick(e, subItem) : undefined}
                              data-testid={`nav-${subItem.name.toLowerCase().replace(' ', '-')}`}
                              className={cn(
                                'flex items-center gap-3 px-4 py-2 rounded-sm font-medium transition-all duration-200',
                                isSubActive
                                  ? 'bg-primary text-primary-foreground shadow-lg'
                                  : subLocked
                                  ? 'text-white/40 hover:bg-white/5'
                                  : 'text-white/70 hover:bg-white/10 hover:text-white'
                              )}
                            >
                              <SubIcon className="h-4 w-4" />
                              <span className="text-xs uppercase tracking-wide">{subItem.name}</span>
                              {subLocked && <Lock className="h-3 w-3 ml-auto" data-testid={`lock-${subItem.name.toLowerCase().replace(' ', '-')}`} />}
                              {!subLocked && isPermissionLocked(subItem.href) && (
                                <Lock className="h-3 w-3 ml-auto text-amber-400" data-testid={`perm-lock-${subItem.name.toLowerCase().replace(' ', '-')}`} title="Locked — ask your head coach for access" />
                              )}
                              {!subLocked && !isPermissionLocked(subItem.href) && isPermissionViewOnly(subItem.href) && (
                                <span className="ml-auto text-[9px] uppercase text-amber-300" data-testid={`perm-view-${subItem.name.toLowerCase().replace(' ', '-')}`} title="View-only">view</span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={isLocked(item.href) ? (e) => handleLockedClick(e, item) : undefined}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-lg'
                      : isLocked(item.href)
                      ? 'text-white/40 hover:bg-white/5'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-sm uppercase tracking-wide">{item.name}</span>
                  {isLocked(item.href) && <Lock className="h-3.5 w-3.5 ml-auto" data-testid={`lock-${item.name.toLowerCase()}`} />}
                  {!isLocked(item.href) && isPermissionLocked(item.href) && (
                    <Lock className="h-3.5 w-3.5 ml-auto text-amber-400" data-testid={`perm-lock-${item.name.toLowerCase()}`} title="Locked — ask your head coach for access" />
                  )}
                  {!isLocked(item.href) && !isPermissionLocked(item.href) && isPermissionViewOnly(item.href) && (
                    <span className="ml-auto text-[9px] uppercase font-bold text-amber-300" data-testid={`perm-view-${item.name.toLowerCase()}`} title="View-only">view</span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-white/10 mt-auto">
            <div className="mb-3 px-2">
              <p className="text-xs text-white/50 uppercase tracking-wider">Logged in as</p>
              <p className="text-sm font-semibold text-white">{user.name}</p>
              <p className="text-xs text-accent capitalize">{user.role.replace('_', ' ')}</p>
            </div>
            <Button
              variant="outline"
              className="w-full bg-white/10 hover:bg-white/20 border-white/20 text-white"
              onClick={onLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              LOGOUT
            </Button>
          </div>
        </aside>

        <main className="flex-1 basketball-court-bg">
          {isLocked(location.pathname) ? (
            <div className="flex items-center justify-center min-h-[70vh] p-8" data-testid="locked-page-guard">
              <div className="text-center max-w-md space-y-4 bg-white/90 rounded-lg p-8 border-2 shadow-lg">
                <Lock className="h-12 w-12 mx-auto text-primary" />
                <h2 className="text-2xl font-bold uppercase">
                  {TIER_LABELS[requiredTier(location.pathname)]} Feature
                </h2>
                <p className="text-muted-foreground">
                  This page requires the {TIER_LABELS[requiredTier(location.pathname)]} plan.
                  Upgrade your subscription to unlock it.
                </p>
                <Link to="/settings">
                  <Button className="w-full mt-2" data-testid="locked-page-upgrade-btn">View Plans in Settings</Button>
                </Link>
              </div>
            </div>
          ) : (
            children
          )}
        </main>
        
        {/* Global Voice Command Button */}
        <GlobalVoiceCommand user={user} />

        {/* Help / Report a problem / Feedback (spec Section 12) */}
        <HelpFeedbackWidget />

        {/* Upgrade prompt for locked nav items */}
        <Dialog open={!!upgradeTarget} onOpenChange={(open) => !open && setUpgradeTarget(null)}>
          <DialogContent className="max-w-sm" data-testid="upgrade-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                {upgradeTarget?.name} is a {TIER_LABELS[requiredTier(upgradeTarget?.href || '/')]} feature
              </DialogTitle>
              <DialogDescription>
                Your current plan is <strong className="capitalize">{tier || 'basic'}</strong>. Upgrade to{' '}
                {TIER_LABELS[requiredTier(upgradeTarget?.href || '/')]} to unlock {upgradeTarget?.name}
                {' '}and more.
              </DialogDescription>
            </DialogHeader>
            <Link to="/settings" onClick={() => setUpgradeTarget(null)}>
              <Button className="w-full" data-testid="upgrade-dialog-btn">View Plans &amp; Upgrade</Button>
            </Link>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}